<template>
  <div :class="$style.mainContainer">
    <app-loader :show-loader="loading" />

    <assign-agent-popup :assign-dialog="assignDialog"
                        :client-id="currentClientId"
                        :client-full-name="currentClientName"
                        :role="currentRole"
                        @close="closeAssignDialog"
                        @change="agentAssigned"/>

    <div :class="$style.clientsHeader">
      <h4>Clients</h4>
      <div :class="$style.groupButtons">
        <app-button button-style="default"
                  text="Export"
                  :class="$style.exportBtn"
                  @click="exportClientsData">
        <app-svg-icon name="ic_export"
                      :class="$style.exportIcon"/>
      </app-button>
      <app-button v-if="showNewClientButton"
                  button-style="accent"
                  text="New"
                  :class="$style.addBtn"
                  @click="openAddClientDialog">
        <app-svg-icon name="ic_add"
                      :class="$style.addIcon"/>
      </app-button>
      </div>
    </div>
    <div :class="$style.tabsContainer">
      <app-tabs v-if="isSuperAdmin"
                :tabsNumber="getTabsNumber"
                @change="changeTab($event)">
        <template v-slot:tabTitle1>
          <span>Action Required</span>
          <span :class="$style.clientsQuantity">
            {{ getActionRequiredClientsQuantity }}
          </span>
        </template>
        <template v-slot:tabTitle2>
          <span>All</span>
          <span :class="$style.clientsQuantity">
            {{ getAllClientsQuantity }}
          </span>
        </template>

        <template  v-slot:tabContent1>
          <admin-portal-table v-if="actionRequiredClients"
                              :headers="clientsHeadersForAdmin"
                              :items="getActionClients"
                              :page-count="actionRequiredClients.metadata.totalPages"
                              :page="page"
                              table-type="clients"
                              @changePage="changeActionRequiredPage($event)"
                              @sortingChanged="changeActionRequiredSorting($event)"
                              @searchByName="searchInActionRequiredByName($event)"
                              @searchByEmail="searchInActionRequiredByEmail($event)"
                              @searchByCoach="searchInActionRequiredByCoach($event)"
                              @searchByNegotiator="searchInActionRequiredByNegotiator($event)"
                              @assignCoach="assignCoach($event)"
                              @assignNegotiator="assignNegotiator($event)">
          </admin-portal-table>
        </template>
        <template v-slot:tabContent2>
          <admin-portal-table v-if="allClients"
                              :headers="clientsHeadersForAdmin"
                              :items="getAllClients"
                              :page-count="allClients.metadata.totalPages"
                              :page="page"
                              table-type="clients"
                              @changePage="changeAllClientsPage($event)"
                              @sortingChanged="changeAllClientsSorting($event)"
                              @searchByName="searchInAllClientsByName($event)"
                              @searchByEmail="searchInAllClientsByEmail($event)"
                              @searchByCoach="searchInAllClientsByCoach($event)"
                              @searchByNegotiator="searchInAllClientsByNegotiator($event)"
                              @assignCoach="assignCoach($event)"
                              @assignNegotiator="assignNegotiator($event)">
          </admin-portal-table>
        </template>
      </app-tabs>

      <app-tabs v-else
                :tabsNumber="getTabsNumber"
                @change="changeTab($event)">
        <template v-slot:tabTitle1>
          <span>All</span>
          <span :class="$style.clientsQuantity">
            {{ getAllClientsQuantity }}
          </span>
        </template>

        <template  v-slot:tabContent1>
          <admin-portal-table v-if="allClients"
                              :headers="clientsHeaders"
                              :items="getAllClients"
                              :page-count="allClients.metadata.totalPages"
                              :page="page"
                              table-type="clients"
                              @changePage="changeAllClientsPage($event)"
                              @sortingChanged="changeAllClientsSorting($event)"
                              @searchByName="searchInAllClientsByName($event)"
                              @searchByEmail="searchInAllClientsByEmail($event)"
                              @searchByCoach="searchInAllClientsByCoach($event)"
                              @searchByNegotiator="searchInAllClientsByNegotiator($event)"
                              @assignCoach="assignCoach($event)"
                              @assignNegotiator="assignNegotiator($event)">
          </admin-portal-table>
        </template>
      </app-tabs>
    </div>

    <v-dialog v-model="addClientDialog" width="500">
      <form :class="$style.addClientForm" @submit.prevent="addNewClient">
        <button :class="$style.closeBtn"
                @click.prevent="closeAddClientDialog($event)">
          <app-svg-icon :class="$style.closeIcon" name="ic_close_black" />
        </button>
        <h4>New Client</h4>
        <app-input-group v-model="$v.addClientDialogValue.firstName.$model"
                          :class="$modals.modalInput"
                          single-line
                          label="First Name"
                          placeholder="First Name"/>
        <app-input-group v-model="$v.addClientDialogValue.lastName.$model"
                          :class="$modals.modalInput"
                          single-line
                          label="Last Name"
                          placeholder="Last Name"/>
        <app-input-group v-model="$v.addClientDialogValue.email.$model"
                          :class="$modals.modalInput"
                          single-line
                          placeholder="Email"
                          label="Email"/>
        <p v-if="$v.addClientDialogValue.email.$error"
           :class="$forms.errorMessage">
          Email is invalid
        </p>
        <div :class="$style.radioGroup">
          <app-radio-buttons v-model="$v.addClientDialogValue.appAccessType.$model"
                              :options="addClientDialogOptions"
                              :value="getProductName"
                              @input="setProductName"/>
          <p v-if="$v.addClientDialogValue.appAccessType.$error"
             :class="$forms.errorMessage">
            Product is required
          </p>
        </div>
        <app-button button-style="accent"
                    text="Create"
                    :class="$forms.authSubmitBtn"
                    :disabled="$v.$invalid"
                    type="submit"/>
        <p v-if="addClientError"
           :class="[$forms.errorMessage, $style.errorMessage]">{{ addClientError }}</p>
      </form>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { required, email } from 'vuelidate/lib/validators';
import { formatDateForAdminTable, forceFileDownload, addZeroToDayOrMonth } from '@/utils';
import AdminPortalTable from '@/app/admin/components/admin-portal-table';
import AssignAgentPopup from '@/app/admin/components/assign-agent-popup';
import AppRadioButtons from '@/app/core/components/app-radio-buttons';
import { RESOLUTIONS, ROLES } from '@/constants';

export default {
  components: {
    AdminPortalTable,
    AssignAgentPopup,
    AppRadioButtons,
  },
  data() {
    return {
      clientsHeaders: [
        {
          text: 'ID',
          value: 'applicationId',
        },
        {
          text: 'Taxpayer Name',
          sortable: false,
          value: 'fullName',
        },
        {
          text: 'Email',
          sortable: false,
          value: 'email',
        },
        {
          text: 'Created At',
          value: 'createdAt',
        },
        {
          text: 'Total Owed',
          sortable: false,
          value: 'totalOwed',
        },
        {
          text: 'Resolution',
          sortable: false,
          value: 'resolution',
        },
      ],
      clientsHeadersForAdmin: [
        {
          text: 'ID',
          value: 'applicationId',
        },
        {
          text: 'Taxpayer Name',
          sortable: false,
          value: 'fullName',
        },
        {
          text: 'Email',
          sortable: false,
          value: 'email',
        },
        {
          text: 'Created At',
          value: 'createdAt',
        },
        {
          text: 'Total Owed',
          sortable: false,
          value: 'totalOwed',
        },
        {
          text: 'Resolution',
          sortable: false,
          value: 'resolution',
        },
        {
          text: 'Coach',
          sortable: false,
          value: 'coachName',
        },
        {
          text: 'Negotiator',
          sortable: false,
          value: 'negotiatorName',
        },
      ],
      loading: false,
      actionRequiredClients: null,
      allClients: null,
      tabSelected: 'tab-1',
      currentClientId: 0,
      currentClientName: '',
      currentClientsActionRequiredOptions: {
        orderBy: 'id',
        page: 1,
        filter: null,
      },
      currentAllClientsOptions: {
        orderBy: 'id',
        page: 1,
        filter: null,
      },
      page: 1,
      currentRole: '',
      assignDialog: false,
      addClientDialog: false,
      addClientDialogOptions: [{
        value: 1,
        label: 'Individual (W-2)',
      },
      {
        value: 2,
        label: 'Business (1099)',
      }],
      addClientDialogValue: {
        firstName: null,
        lastName: null,
        email: null,
        appAccessType: null,
      },
      addClientError: null,
    };
  },
  methods: {
    ...mapActions([
      'fetchClients',
      'fetchClientsActionRequired',
      'exportClients',
      'exportClientsActionRequired',
      'addClient',
    ]),
    setProductName(appAccessType) {
      this.addClientDialogValue.appAccessType = appAccessType;
      if (!appAccessType) {
        this.addClientDialogValue.appAccessType = null;
      }
    },
    getProductName() {
      return this.addClientDialogValue.appAccessType;
    },
    changeTab(e) {
      this.tabSelected = e;
      this.page = 1;
      if (this.currentClientsActionRequiredOptions.page !== 1) {
        this.currentClientsActionRequiredOptions.page = 1;
        this.getActionRequiredClientsWithQuery(this.currentClientsActionRequiredOptions, 1);
      }
      if (this.currentAllClientsOptions.page !== 1) {
        this.currentAllClientsOptions.page = 1;
        this.getAllClientsWithQuery(this.currentAllClientsOptions, 1);
      }
    },
    formatCurrentDate() {
      const dateStr = new Date();
      const month = addZeroToDayOrMonth(dateStr.getMonth() + 1);
      const day = addZeroToDayOrMonth(dateStr.getDate());
      const year = dateStr.getFullYear();
      return `${year}-${month}-${day}`;
    },
    exportClientsData() {
      if (this.tabSelected === 'tab-1' && this.isAdmin) {
        const options = this.currentClientsActionRequiredOptions;
        this.exportClientsActionRequired(options)
          .then((res) => {
            const date = this.formatCurrentDate();
            forceFileDownload(res, `Clients_${date}.csv`);
          });
      } else {
        const options = this.currentAllClientsOptions;
        this.exportClients(options)
          .then((res) => {
            const date = this.formatCurrentDate();
            forceFileDownload(res, `Clients_${date}.csv`);
          });
      }
    },
    openAddClientDialog() {
      this.resetAddClientDialog();
      this.addClientDialog = true;
    },
    closeAddClientDialog() {
      this.addClientDialog = false;
    },
    resetAddClientDialog() {
      this.addClientDialogValue.firstName = null;
      this.addClientDialogValue.lastName = null;
      this.addClientDialogValue.email = null;
      this.addClientDialogValue.appAccessType = null;
      this.$v.$reset();
    },
    addNewClient(e) {
      this.addClientError = null;
      const submitBtn = e.target;
      submitBtn.disabled = true;
      const agentData = {
        ...this.addClientDialogValue,
      };
      this.addClient(agentData)
        .then((res) => {
          console.log(res);
          this.fetchAllTypesOfClients();
          this.closeAddClientDialog();
        })
        .catch((err) => {
          console.log(err);
          this.addClientError = err.response.data.title;
        })
        .finally(() => {
          submitBtn.disabled = false;
        });
    },
    assignCoach(client) {
      this.currentRole = 'Coach';
      this.currentClientName = client.fullName;
      this.currentClientId = client.applicationId;
      this.assignDialog = true;
    },
    assignNegotiator(client) {
      this.currentRole = 'Negotiator';
      this.currentClientName = client.fullName;
      this.currentClientId = client.applicationId;
      this.assignDialog = true;
    },
    closeAssignDialog() {
      this.assignDialog = false;
      this.currentRole = '';
      this.currentClientName = '';
      this.currentClientId = 0;
    },
    agentAssigned() {
      this.currentClientsActionRequiredOptions.page = 1;
      this.currentAllClientsOptions.page = 1;
      this.getActionRequiredClientsWithQuery(this.currentClientsActionRequiredOptions, 1);
      this.getAllClientsWithQuery(this.currentAllClientsOptions, 1);
    },
    fetchAllTypesOfClients() {
      this.getAllClientsWithQuery(this.currentAllClientsOptions);
      this.getActionRequiredClientsWithQuery(this.currentClientsActionRequiredOptions);
    },
    getAllClientsWithQuery(query, page) {
      this.loading = true;
      this.fetchClients(query)
        .then(() => {
          this.page = page;
          this.allClients = this.getClients;
        })
        .catch((err) => {
          console.error(err.response);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    getActionRequiredClientsWithQuery(query, page) {
      this.loading = true;
      this.fetchClientsActionRequired(query)
        .then(() => {
          this.page = page;
          this.actionRequiredClients = this.getClientsActionRequired;
        })
        .catch((err) => {
          console.error(err.response);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    changeActionRequiredPage(e) {
      this.currentClientsActionRequiredOptions = {
        ...this.currentClientsActionRequiredOptions,
        page: e,
      };
      this.getActionRequiredClientsWithQuery(this.currentClientsActionRequiredOptions, e);
    },
    changeAllClientsPage(e) {
      this.currentAllClientsOptions = {
        ...this.currentAllClientsOptions,
        page: e,
      };
      this.getAllClientsWithQuery(this.currentAllClientsOptions, e);
    },
    changeActionRequiredSorting(e) {
      let sortingOption = e.orderBy;
      if (e.orderBy === 'applicationId') {
        sortingOption = 'id';
      }
      if (e.desc) {
        sortingOption = `${sortingOption}:desc`;
      }
      if (!sortingOption) {
        sortingOption = 'id';
      }
      this.currentClientsActionRequiredOptions = {
        ...this.currentClientsActionRequiredOptions,
        page: 1,
        orderBy: sortingOption,
      };
      this.getActionRequiredClientsWithQuery(this.currentClientsActionRequiredOptions, 1);
    },
    changeAllClientsSorting(e) {
      let sortingOption = e.orderBy;
      if (e.orderBy === 'applicationId') {
        sortingOption = 'id';
      }
      if (e.desc) {
        sortingOption = `${sortingOption}:desc`;
      }
      if (!sortingOption) {
        sortingOption = 'id';
      }
      this.currentAllClientsOptions = {
        ...this.currentAllClientsOptions,
        page: 1,
        orderBy: sortingOption,
      };
      this.getAllClientsWithQuery(this.currentAllClientsOptions, 1);
    },
    searchInActionRequiredByName(e) {
      this.currentClientsActionRequiredOptions = {
        ...this.currentClientsActionRequiredOptions,
        page: 1,
        filter: {
          filterName: 'TaxpayerNameSearchQuery',
          filterQuery: e,
        },
      };
      this.getActionRequiredClientsWithQuery(this.currentClientsActionRequiredOptions, 1);
    },
    searchInActionRequiredByEmail(e) {
      const re = /\+/gi;
      const queryString = e.replace(re, '%2B');
      this.currentClientsActionRequiredOptions = {
        ...this.currentClientsActionRequiredOptions,
        page: 1,
        filter: {
          filterName: 'TaxpayerEmailSearchQuery',
          filterQuery: queryString,
        },
      };
      this.getActionRequiredClientsWithQuery(this.currentClientsActionRequiredOptions, 1);
    },
    searchInActionRequiredByCoach(e) {
      this.currentClientsActionRequiredOptions = {
        ...this.currentClientsActionRequiredOptions,
        page: 1,
        filter: {
          filterName: 'CoachSearchQuery',
          filterQuery: e,
        },
      };
      this.getActionRequiredClientsWithQuery(this.currentClientsActionRequiredOptions, 1);
    },
    searchInActionRequiredByNegotiator(e) {
      this.currentClientsActionRequiredOptions = {
        ...this.currentClientsActionRequiredOptions,
        page: 1,
        filter: {
          filterName: 'NegotiatorSearchQuery',
          filterQuery: e,
        },
      };
      this.getActionRequiredClientsWithQuery(this.currentClientsActionRequiredOptions, 1);
    },
    searchInAllClientsByName(e) {
      this.currentAllClientsOptions = {
        ...this.currentAllClientsOptions,
        page: 1,
        filter: {
          filterName: 'TaxpayerNameSearchQuery',
          filterQuery: e,
        },
      };
      this.getAllClientsWithQuery(this.currentAllClientsOptions, 1);
    },
    searchInAllClientsByEmail(e) {
      const re = /\+/gi;
      const queryString = e.replace(re, '%2B');
      this.currentAllClientsOptions = {
        ...this.currentAllClientsOptions,
        page: 1,
        filter: {
          filterName: 'TaxpayerEmailSearchQuery',
          filterQuery: queryString,
        },
      };
      this.getAllClientsWithQuery(this.currentAllClientsOptions, 1);
    },
    searchInAllClientsByCoach(e) {
      this.currentAllClientsOptions = {
        ...this.currentAllClientsOptions,
        page: 1,
        filter: {
          filterName: 'CoachSearchQuery',
          filterQuery: e,
        },
      };
      this.getAllClientsWithQuery(this.currentAllClientsOptions, 1);
    },
    searchInAllClientsByNegotiator(e) {
      this.currentAllClientsOptions = {
        ...this.currentAllClientsOptions,
        page: 1,
        filter: {
          filterName: 'NegotiatorSearchQuery',
          filterQuery: e,
        },
      };
      this.getAllClientsWithQuery(this.currentAllClientsOptions, 1);
    },
  },
  computed: {
    ...mapGetters([
      'getClients',
      'getClientsActionRequired',
      'getRoles',
    ]),
    getAllClients() {
      if (this.getClients) {
        return this.getClients.items.map(client => ({
          ...client,
          createdAt: formatDateForAdminTable(client.createdAt),
          resolution: RESOLUTIONS.find(item => item.code === client.resolution).textValue,
          totalOwed: `$${client.totalOwed.toFixed(2)}`,
        }));
      }

      return [];
    },
    getActionClients() {
      if (this.getClientsActionRequired) {
        return this.getClientsActionRequired.items.map(client => ({
          ...client,
          createdAt: formatDateForAdminTable(client.createdAt),
          resolution: RESOLUTIONS.find(item => item.code === client.resolution).textValue,
          totalOwed: `$${client.totalOwed.toFixed(2)}`,
        }));
      }

      return [];
    },
    getAllClientsQuantity() {
      return this.getClients ? this.getClients.metadata.totalCount : 0;
    },
    getActionRequiredClientsQuantity() {
      return this.getClientsActionRequired ? this.getClientsActionRequired.metadata.totalCount : 0;
    },
    isSuperAdmin() {
      const roles = this.getRoles;
      return roles.includes(ROLES.SUPER_ADMIN);
    },
    getTabsNumber() {
      const roles = this.getRoles;
      return roles.includes(ROLES.SUPER_ADMIN) ? 2 : 1;
    },
    showNewClientButton() {
      const roles = this.getRoles;
      return roles.includes(ROLES.ADMIN) || roles.includes(ROLES.CPA);
    },
  },
  created() {
    this.fetchAllTypesOfClients();
  },
  validations: {
    addClientDialogValue: {
      firstName: {
        required,
      },
      lastName: {
        required,
      },
      email: {
        required,
        email,
      },
      appAccessType: {
        required,
      },
    },
  },
};
</script>
<style src="@/styles/modals.scss" lang="scss" module="$modals"></style>
<style src="@/styles/forms.scss" lang="scss" module="$forms"></style>
<style lang="scss" module>
@import "@/styles/variables.scss";

.mainContainer {
  width: 100%;
  margin: 40px 50px;
}

.tabsContainer {
  width: 100%;
}

.clientsQuantity {
  width: 40px;
  height: 20px;
  line-height: 20px;
  display: inline-block;
  border-radius: 9px;
  text-align: center;
  background-color: $adminTablesColor;
}

.clientsHeader {
  width: 100%;
  display: flex;
  justify-content: space-between;
  text-align: center;
  padding-bottom: 18px;
  border-bottom: 1px solid $adminTablesColor;
  margin-bottom: 20px;

  h4 {
    color: $adminPortalTitlesColor;
    font-size: 2.8rem;
    line-height: 3rem;
  }
}

.groupButtons{
  display: flex;
  flex-direction: row;
}

.exportBtn {
  padding: 8px 40px;
  margin-right: 20px;

  .exportIcon {
    width: 16px;
    height: 16px;
    margin-right: 6px;
  }
}

.addBtn {
  padding: 8px 35px;
  display: flex;
  align-items: center;
  flex-direction: row-reverse;

  .addIcon {
    width: 20px;
    height: 20px;
    fill: $white-color;
  }
}

.addClientForm {
  padding: 40px 20px 34px;
  text-align: center;
  position: relative;

  .radioGroup {
    padding: 24px 0 0 0;
  }

  .closeBtn {
    position: absolute;
    top: 10px;
    right: 10px;
    width: 16px;
    height: 16px;
    border: none;
    outline: none;
  }

  .closeIcon {
    width: 16px;
    height: 16px;
  }

  .errorMessage {
    padding: 20px 0 0 0;
  }
}
</style>
